/**
 * Estimation Dashboard — Casper Dashboard-inspired hub for one estimate.
 * Completeness, cost-type mix, interconnect status, AI curve.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCbApi, useCbConfig } from './cbApi';
import {
  SheetShell, HeaderButton, HelpModal,
  LoadingState, ErrorState, NoEstimateState, useToast, fmt,
} from './CbUi';
import AiAccuracyCurve from './AiAccuracyCurve';
import CbAuditLogButton from './CbAuditLogButton';

const ACCENT = '#1e40af';

const EstimationDashboard = ({ estimate, onNavigate }) => {
  const api = useCbApi();
  const { config } = useCbConfig();
  const { ToastEl } = useToast();
  const [onePager, setOnePager] = useState(null);
  const [sov, setSov] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    if (!estimate?.id) return;
    setLoading(true);
    try {
      const [op, roll] = await Promise.all([
        api.get(`/cost-breakdown/one-pager/${estimate.id}`),
        api.get(`/cost-breakdown/client-wbs/rollup/${estimate.id}`).catch(() => null),
      ]);
      setOnePager(op.data);
      setSov(roll?.data || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id]);

  useEffect(() => { load(); }, [load]);

  const costTypes = config?.costTypes || [];
  const totals = onePager?.totals || { man_hours: 0, dollar_cost: 0, selling: 0 };
  const ctTotals = onePager?.costTypeTotals || {};

  const maxCost = useMemo(() => {
    let m = 1;
    for (const ct of costTypes) {
      m = Math.max(m, Number(ctTotals[ct.key]?.dollar_cost) || 0);
    }
    return m;
  }, [costTypes, ctTotals]);

  const checklist = useMemo(() => {
    const hasSummary = (totals.dollar_cost || 0) > 0 || (totals.man_hours || 0) > 0;
    const hasIndirect = (Number(ctTotals.indirects?.dollar_cost) || 0) > 0;
    const hasSite = (Number(ctTotals.site_expense?.dollar_cost) || 0) > 0;
    const hasSov = (sov?.codesWithCost || 0) > 0 || (sov?.lines || []).length > 0;
    const fxOk = !!(onePager?.currency?.is_frozen);
    return [
      { id: 'summary', label: 'Discipline costs on CB Summary', ok: hasSummary, go: 'cost-breakdown-summary' },
      { id: 'indirect', label: 'Indirects populated', ok: hasIndirect, go: 'cost-breakdown-indirects' },
      { id: 'site', label: 'Site expenses populated', ok: hasSite, go: 'cost-breakdown-site-expenses' },
      { id: 'sov', label: 'SOV / client WBS mapped', ok: hasSov, go: 'sov' },
      { id: 'fx', label: 'CAD→USD rate frozen (for USD view)', ok: fxOk, go: 'currency-exchange' },
    ];
  }, [totals, ctTotals, sov, onePager]);

  if (!estimate?.id) return <NoEstimateState section="Estimation Dashboard" />;
  if (loading && !onePager) return <LoadingState label="Loading dashboard…" />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  return (
    <>
      <SheetShell
        title="📊 Estimation Dashboard"
        subtitle={`Estimate ${estimate.estimate_number} · ${estimate.estimate_name || ''} · package health & interconnect status`}
        gradient={`linear-gradient(135deg, ${ACCENT}, #3b82f6)`}
        headerActions={
          <>
            <HeaderButton onClick={load}>🔄 Refresh</HeaderButton>
            <CbAuditLogButton
              tableName={['cb_discipline_detail', 'cb_discipline_summary', 'cb_indirects', 'cb_site_expenses', 'cb_client_wbs', 'cb_crew_calculation']}
              estimateId={estimate.id}
              title="Estimate Audit Log"
              accentColor={ACCENT}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={[
          { label: 'Man Hours', value: fmt(totals.man_hours, 'number'), icon: '⏱️', color: '#059669' },
          { label: 'Package Cost', value: fmt(totals.dollar_cost, 'currency'), icon: '💰', color: '#dc2626' },
          { label: 'Selling', value: fmt(totals.selling, 'currency'), icon: '🏷️', color: ACCENT },
          { label: 'SOV codes with $', value: sov?.codesWithCost ?? 0, icon: '🗂️', color: '#7c3aed' },
        ]}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.4fr)', gap: 16, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12, color: '#0f172a' }}>Completeness checklist</div>
            {checklist.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onNavigate && onNavigate(c.go)}
                style={{
                  display: 'flex', width: '100%', alignItems: 'center', gap: 10,
                  padding: '10px 12px', marginBottom: 8, border: '1px solid #e2e8f0',
                  borderRadius: 10, background: c.ok ? '#f0fdf4' : '#fff', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 18 }}>{c.ok ? '✅' : '⬜'}</span>
                <span style={{ flex: 1, fontSize: 13.5, color: '#334155' }}>{c.label}</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>Open →</span>
              </button>
            ))}
            {sov?.uncodedDetail?.rows > 0 && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: '#b45309', background: '#fffbeb', padding: 10, borderRadius: 8 }}>
                {sov.uncodedDetail.rows} detail row(s) have cost but no WBS/SOV tag — tag them then sync SOV.
              </div>
            )}
          </div>

          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12, color: '#0f172a' }}>Cost type mix</div>
            {costTypes.map((ct) => {
              const cost = Number(ctTotals[ct.key]?.dollar_cost) || 0;
              const pct = (cost / maxCost) * 100;
              return (
                <div key={ct.key} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                    <span style={{ color: '#475569' }}>{ct.label || ct.key}</span>
                    <strong>{fmt(cost, 'currency')}</strong>
                  </div>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: ACCENT, borderRadius: 999, minWidth: cost ? 4 : 0 }} />
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {[
                ['cost-breakdown-summary', 'CB Summary'],
                ['cost-breakdown-rentals', 'Rentals → Pull'],
                ['estimation-crew-calculation', 'Crew pull'],
                ['sov', 'SOV sync'],
                ['estimation-ai-curve', 'AI Curve'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => onNavigate && onNavigate(id)}
                  style={{
                    padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1',
                    background: '#f8fafc', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#1e293b',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <AiAccuracyCurve estimate={estimate} />
        {ToastEl}
      </SheetShell>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Estimation Dashboard Help" accent={ACCENT} guideId="cb-dashboard-help">
        <p>This dashboard is the portal counterpart to Casper's <strong>Dashboard</strong> tab: package health, cost mix, and shortcuts into the interconnect pulls (Rentals → estimate, Crew ← summaries, SOV SUMIFS).</p>
        <p>Use the checklist to walk the bid before you open the one-pager and Bid Package.</p>
      </HelpModal>
    </>
  );
};

export default EstimationDashboard;
